import { TFunction } from 'i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
// eslint-disable-next-line no-restricted-imports
import { useEffect, useState } from 'react';
import { ZodError } from 'zod';
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  GridAlpha,
  Heading,
  Notice,
  PhoneInput,
  RadioGroup,
  Select,
  Text,
  TextInput,
  createForm,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import { ToastInterface, zodValidateField } from '../../../src/common/schema/forms';
import { Language } from '../../../src/locales/Language';
import TdBanner from '../../assets/TdGicBanner.png';
import { VasError } from '../../common/errors/VasError';
import { useGetTrackingEventData, useQueryParams } from '../../common/hooks/useQueryParams';
import { getCountryLabel } from '../../common/utils/getCountryLabel';
import {
  CreateTdGicApplicationReqV2Body,
  CreateTdGicApplicationResV2Body,
  formDataToCreateBody,
  useCreateTdGicApplicationV2,
} from '../../data/tdGic';
import {
  TD_GIC_FORM_DEFAULT_VALUES,
  TdPrefix,
  TdSupportedCountriesOfResidence,
} from './FormStateMapper';
import {
  countryCodeSchema,
  emailAddressSchema,
  nameSchema,
  nationalPhoneNumberSchema,
  phoneNumberSchema,
  tdGicFormSchema,
} from './TdFormSchemas';
import { DEFAULT_MIN_STRING_LENGTH, ENTITY_ID_MAX_LENGTH } from './constants';
import { TdFormEvents } from './types';

interface TdGicFormProps {
  preFillFormState?: TdFormState;
  studentId?: string;
  isStudentDetailsError?: boolean;
  isCurrentUserApplyingForThemselves?: boolean;
  validateValuesOnLoad?: boolean;
}

export interface TdFormState {
  prefix: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  emailAddress: string;
  countryOfResidence: string;
  preferredLanguage: string;
  isConsentToApplyboardTermsAndConditions: boolean;
  isAgreeToApplyBoardCommunications: boolean;
  dateOfBirth: string;
  confirmDateOfBirth: string;
  isAgreeToTdAgreements: boolean;
  nationalPhoneNumber: string;
  countryCallingCode: string;
}

const { Field, Form, useSetFieldValues, useTriggerValidation } = createForm<TdFormState>();

function TdPhoneInput({
  validateOnLoad,
  toasts,
  t,
}: {
  validateOnLoad: boolean;
  toasts: ToastInterface;
  t: TFunction;
}) {
  const setFormValues = useSetFieldValues();
  return (
    <Field
      as={PhoneInput}
      name="mobileNumber"
      label={t('td.fieldLabels.mobileNumber')}
      required={validateOnLoad ? false : t('td.errors.mobileNumberIsRequiredAndMustBeValid')}
      placeholder=""
      validate={value => {
        if (validateOnLoad && !value) {
          return true;
        }
        // Validate the full phone number(ex: +13333333333)
        const result = zodValidateField(phoneNumberSchema(t), 'mobileNumber', value, toasts);
        if (result === true) {
          // Parse the phone number into country code (ex: +1) and national phone number (ex: 3333333333)
          const parsedPhoneNumber = parsePhoneNumberFromString(value);

          if (!parsedPhoneNumber?.isPossible()) {
            return t('td.errors.mobileNumberIsRequiredAndMustBeValid');
          } else {
            if (!parsedPhoneNumber?.isValid()) {
              toasts.negative(new Error(t('td.errors.mobileNumberIsRequiredAndMustBeValid')));
              return t('td.errors.mobileNumberIsRequiredAndMustBeValid');
            }
          }

          const nationalPhoneNumber = parsedPhoneNumber?.nationalNumber?.toString();
          const countryCallingCode = '+' + parsedPhoneNumber?.countryCallingCode?.toString();
          // Validate the national phone number
          const nationalPhoneResult = zodValidateField(
            nationalPhoneNumberSchema(t),
            'mobileNumber',
            nationalPhoneNumber,
            toasts,
          );
          // Validate the country code
          const countryCodeResult = zodValidateField(
            countryCodeSchema(t),
            'mobileNumber',
            countryCallingCode,
            toasts,
          );
          // Set the parsed phone number to the form values
          setFormValues({ nationalPhoneNumber, countryCallingCode });
          const isParsedPhoneNumberValid =
            nationalPhoneResult === true && countryCodeResult === true;
          const parsedPhoneNumberErrorMessage =
            typeof nationalPhoneResult === 'string' ? nationalPhoneResult : countryCodeResult;

          return isParsedPhoneNumberValid || parsedPhoneNumberErrorMessage;
        }

        return result;
      }}
    />
  );
}

function OnLoadValidateOnlyNonEmptyFields({
  validateOnLoad,
  setValidateOnLoad,
}: {
  validateOnLoad: boolean;
  setValidateOnLoad: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const triggerValidation = useTriggerValidation();
  if (validateOnLoad) {
    triggerValidation().then(() => setValidateOnLoad(false));
  }
  return <></>;
}

const TdGicForm = ({
  preFillFormState = TD_GIC_FORM_DEFAULT_VALUES,
  studentId,
  isStudentDetailsError = false,
  isCurrentUserApplyingForThemselves = false,
  validateValuesOnLoad = false,
}: TdGicFormProps) => {
  const { t, i18n } = useTranslation();
  const formId = useId();
  const toasts = useToasts();

  const entityIdRegex = new RegExp(`^[0-9]{${DEFAULT_MIN_STRING_LENGTH},${ENTITY_ID_MAX_LENGTH}}$`);
  const { isCreatingTdGicApplication, createTdGicApplication } = useCreateTdGicApplicationV2();
  const [showStudentDetailsError, setShowStudentDetailsError] = useState(isStudentDetailsError);
  const [validateOnLoad, setValidateOnLoad] = useState(validateValuesOnLoad);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const { trackEvent } = useEventTracker();
  const query = useQueryParams();
  const language = query.get('language') ?? Language.enUS;
  const { rpId: referrerId, utmSource, source } = useGetTrackingEventData();

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  useEffect(() => {
    trackEvent({
      event: TdFormEvents.PageView,
      data: { rp_id: referrerId, utm_source: utmSource, source: source },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (showStudentDetailsError) {
    if (isCurrentUserApplyingForThemselves) {
      toasts.negative(new Error(t('landingPage.errorMessages.unableToFetchYourDetails')));
    } else {
      toasts.negative(new Error(t('landingPage.errorMessages.unableToFetchStudentDetails')));
    }
    setShowStudentDetailsError(false);
  }

  if (studentId) {
    if (!entityIdRegex.test(studentId.trim())) {
      toasts.warning(t('td.errors.invalidStudentId'));
      studentId = undefined;
    }
  }

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        maxWidth: '1024px',
        margin: '0 auto',
      }}
    >
      <Card>
        <Card.Image alt="TD GIC Banner" src={TdBanner} />
        <Card.Header>
          <Heading intent="secondary" variant="titleS" align="center" level={1}>
            {t('td.informationDisclaimer')}
            <sup>{t('td.copyrightSymbol')}</sup>
          </Heading>
        </Card.Header>
        <Form
          id={formId}
          defaultValues={preFillFormState}
          onError={() => {
            toasts.negative(new Error(t('td.errors.errorOnForm')));
          }}
          onSubmit={async formValues => {
            setSubmitDisabled(true);
            if (referrerId) {
              if (!entityIdRegex.test(referrerId.trim())) {
                // Checks if query param is invalid ahead of time so user gets clear feedback instead of generic error
                toasts.negative(new Error(t('td.errors.invalidRpId')));
                setSubmitDisabled(false);
                return;
              }
            }

            let createBody: CreateTdGicApplicationReqV2Body;
            try {
              const parsedFormValues = tdGicFormSchema(t).parse(formValues);
              createBody = await formDataToCreateBody(
                parsedFormValues,
                referrerId || undefined,
                studentId || undefined,
              );
            } catch (e) {
              setSubmitDisabled(false);
              trackEvent({
                event: TdFormEvents.SubmissionErrored,
                data: { rp_id: referrerId, utm_source: utmSource, source: source },
              });

              const error = new Error(`TD GIC Submission failure. Reason ${e}`);
              trackEvent({
                event: 'TD GIC submission error',
                error,
              });

              if (e instanceof ZodError) {
                toasts.negative(new Error(t('common.errors.invalidSubmission')));
                return;
              }
              toasts.negative(new Error(t('common.errors.unexpected')));
              return;
            }

            createTdGicApplication(
              createBody,
              (response: CreateTdGicApplicationResV2Body) => {
                trackEvent({
                  event: TdFormEvents.SubmissionCompleted,
                  data: { rp_id: referrerId, utm_source: utmSource, source: source },
                });
                toasts.positive(t('td.applicationSubmitted'));
                if (response?.data?.meta?.vendorRedirectUrl) {
                  window.location.href = response.data.meta.vendorRedirectUrl;
                }
              },
              error => {
                setSubmitDisabled(false);
                console.error('error: ', error);
                let errorMessage = t('common.errors.submission');

                if (error instanceof VasError) {
                  const vasError: VasError = error;
                  errorMessage =
                    error.errorBody.message instanceof Array
                      ? error.errorBody.message.join(',')
                      : error.errorBody.message;

                  if (vasError.errorBody.statusCode === 409) {
                    errorMessage = t('td.errors.http409');
                  }
                }

                trackEvent({
                  event: TdFormEvents.SubmissionErrored,
                  data: {
                    rp_id: referrerId,
                    utm_source: utmSource,
                    source: source,
                    error: errorMessage,
                  },
                });

                toasts.negative(new Error(errorMessage));
              },
            );
          }}
        >
          <GridAlpha columns="repeat(13, 1fr)" columnGap={4} rowGap={14} pt={2}>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 3,
                md: 4,
              }}
            >
              <Field
                appearance="native"
                as={Select}
                name="prefix"
                label={t('td.fieldLabels.prefix')}
                required={validateOnLoad ? false : t('td.errors.prefixIsRequired')}
                placeholder={t('td.fieldLabels.select')}
                validate={value => {
                  if (validateOnLoad && !value) {
                    return true;
                  }
                  if (!Object.values(TdPrefix).includes(value as TdPrefix)) {
                    return t('td.errors.prefixIsRequired');
                  }
                  return true;
                }}
              >
                {[
                  { label: t('td.fieldLabels.mr'), value: TdPrefix.Mr },
                  { label: t('td.fieldLabels.ms'), value: TdPrefix.Ms },
                  { label: t('td.fieldLabels.mrs'), value: TdPrefix.Mrs },
                ].map(opt => (
                  <Select.Option {...opt} key={opt.value} />
                ))}
              </Field>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 7,
              }}
            >
              <Field
                as={TextInput}
                name="firstName"
                label={t('td.fieldLabels.firstName')}
                required={validateOnLoad ? false : t('td.errors.nameIsRequiredAndMustBeValid')}
                placeholder=""
                validate={value =>
                  zodValidateField(nameSchema(t), 'firstName', value, toasts, validateOnLoad)
                }
              />
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 8,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Field
                as={TextInput}
                name="lastName"
                label={t('td.fieldLabels.lastName')}
                required={validateOnLoad ? false : t('td.errors.nameIsRequiredAndMustBeValid')}
                placeholder=""
                validate={value =>
                  zodValidateField(nameSchema(t), 'lastName', value, toasts, validateOnLoad)
                }
              />
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 7,
              }}
            >
              <TdPhoneInput validateOnLoad={validateOnLoad} toasts={toasts} t={t} />
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 8,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Field
                as={TextInput}
                name="emailAddress"
                label={t('td.fieldLabels.emailAddress')}
                required={
                  validateOnLoad ? false : t('td.errors.emailAddressIsRequiredAndMustBeValid')
                }
                placeholder=""
                validate={value =>
                  zodValidateField(
                    emailAddressSchema(t),
                    'emailAddress',
                    value,
                    toasts,
                    validateOnLoad,
                  )
                }
              />
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 7,
              }}
            >
              <Field
                appearance="native"
                as={Select}
                name="countryOfResidence"
                label={t('td.fieldLabels.countryOfResidence')}
                required={validateOnLoad ? false : t('td.errors.countryOfResidenceIsRequired')}
                placeholder={t('td.fieldLabels.select')}
                validate={value => {
                  if (validateOnLoad && !value) {
                    return true;
                  }
                  if (
                    !Object.values(TdSupportedCountriesOfResidence).includes(
                      value as TdSupportedCountriesOfResidence,
                    )
                  ) {
                    return `${t('td.errors.invalidCountry')}: ${getCountryLabel(value)}`;
                  }
                  return true;
                }}
              >
                {[
                  {
                    label: t('td.fieldLabels.supportedCountries.CN'),
                    value: TdSupportedCountriesOfResidence.China,
                  },
                  {
                    label: t('td.fieldLabels.supportedCountries.IN'),
                    value: TdSupportedCountriesOfResidence.India,
                  },
                  {
                    label: t('td.fieldLabels.supportedCountries.MA'),
                    value: TdSupportedCountriesOfResidence.Morocco,
                  },
                  {
                    label: t('td.fieldLabels.supportedCountries.PK'),
                    value: TdSupportedCountriesOfResidence.Pakistan,
                  },
                  {
                    label: t('td.fieldLabels.supportedCountries.PH'),
                    value: TdSupportedCountriesOfResidence.Philippines,
                  },
                  {
                    label: t('td.fieldLabels.supportedCountries.SN'),
                    value: TdSupportedCountriesOfResidence.Senegal,
                  },
                  {
                    label: t('td.fieldLabels.supportedCountries.VN'),
                    value: TdSupportedCountriesOfResidence.Vietnam,
                  },
                ].map(opt => (
                  <Select.Option {...opt} key={opt.value} />
                ))}
              </Field>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Field
                as={RadioGroup}
                name="preferredLanguage"
                label={t('td.languageSelection.preferredLanguage')}
                required={validateOnLoad ? false : t('td.errors.preferredLanguageIsRequired')}
                validate={value => {
                  if (validateOnLoad && !value) {
                    return true;
                  }
                  if (!Object.values(Language).includes(value as Language)) {
                    return t('td.errors.preferredLanguageIsRequired');
                  }
                  return true;
                }}
              >
                <RadioGroup.Option label={t('td.languageSelection.enUS')} value={Language.enUS} />
                <RadioGroup.Option label={t('td.languageSelection.frCA')} value={Language.frCA} />
              </Field>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Notice actionText="" intent="secondary" width="fill">
                <Text intent="secondary" align="start" as="p" variant="bodyM">
                  {t('td.applyBoardAgreements.header')}
                  <br />
                  <br />
                  {t('td.applyBoardAgreements.details')}
                </Text>
              </Notice>
              <br />
              <Field
                as={Checkbox}
                name="isConsentToApplyboardTermsAndConditions"
                label={t('td.applyBoardAgreements.consentToCollectionOfPersonalInformation')}
                labelPlacement="right"
                required={validateOnLoad ? false : t('td.errors.consentToApplyBoardTosIsRequired')}
              />
              <Field
                as={Checkbox}
                name="isAgreeToApplyBoardCommunications"
                label={t('td.applyBoardAgreements.agreeToOtherCommunicationsFromApplyBoard')}
                labelPlacement="right"
              />
              <br />
              <Text intent="secondary" align="start">
                {t('td.applyBoardAgreements.communcationsUnsubscribeDetails')}
              </Text>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 4,
                md: 6,
              }}
            >
              <Field
                as={DatePicker}
                name="dateOfBirth"
                label={t('td.fieldLabels.dateOfBirth')}
                required={validateOnLoad ? false : t('td.errors.dateOfBirthIsRequired')}
                granularity="day"
                minDate="1900-01-01T00:00:00.000Z"
                maxDate={new Date().toISOString()}
                size="md"
                validate={value => {
                  if (validateOnLoad && !value) {
                    return true;
                  }
                  if (!value) {
                    return t('td.errors.dateOfBirthIsRequired');
                  }

                  const maxDate = new Date(new Date().setFullYear(new Date().getFullYear() - 18));
                  const currentDate = new Date(value);

                  if (currentDate.getTime() > maxDate.getTime()) {
                    return t('td.errors.dateOfBirthUserHasLessThan18Years');
                  }
                  return true;
                }}
              ></Field>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 4,
                md: 6,
              }}
            >
              <Field
                as={DatePicker}
                name="confirmDateOfBirth"
                label={t('td.fieldLabels.confirmDateOfBirth')}
                required={validateOnLoad ? false : t('td.errors.dateOfBirthIsRequired')}
                granularity="day"
                minDate="1900-01-01T00:00:00.000Z"
                maxDate={new Date().toISOString()}
                size="md"
                validate={(value, { values }) => {
                  if (validateOnLoad && !value) {
                    return true;
                  }
                  if (!value) {
                    return t('td.errors.dateOfBirthIsRequired');
                  }

                  const maxDate = new Date(new Date().setFullYear(new Date().getFullYear() - 18));
                  const currentDate = new Date(value);
                  const confirmDateOfBirth = value.split('T')[0];
                  const dateOfBirth = values.dateOfBirth.split('T')[0];
                  if (currentDate.getTime() > maxDate.getTime()) {
                    return t('td.errors.dateOfBirthUserHasLessThan18Years');
                  }
                  if (dateOfBirth !== confirmDateOfBirth) {
                    return t('td.errors.dateOfBirthMustMatch');
                  }

                  return true;
                }}
              ></Field>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" align="start">
                {t('td.dateOfBirthInformation')}
              </Text>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Notice actionText="" intent="secondary" width="fill">
                <Text intent="secondary" align="start" as="p" variant="bodyM">
                  {t('td.tdAgreements.header')}
                  <br />
                  <br />
                  {t('td.tdAgreements.line1')}
                  <br />
                  <br />
                  {t('td.tdAgreements.line2')}
                  <br />
                  <br />
                  {t('td.tdAgreements.line3')}
                  <br />
                  <br />
                  {t('td.tdAgreements.line4')}
                  <sup>{t('td.tdAgreements.superscript1')}</sup>
                  <br />
                  <br />
                </Text>
                <Text intent="secondary" align="start" as="p" variant="bodyS">
                  <i>
                    <sup>{t('td.tdAgreements.superscript1')}</sup>
                    {t('td.tdAgreements.definitionLine1')}
                  </i>
                  <br />
                  <br />
                  <i>{t('td.tdAgreements.definitionLine2')}</i>
                </Text>
              </Notice>
              <br />
              <Field
                as={Checkbox}
                name="isAgreeToTdAgreements"
                label={t('td.tdAgreements.agree')}
                labelPlacement="right"
                required={validateOnLoad ? false : t('td.errors.agreementToTdConsentsIsRequired')}
              />
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Button
                intent="primary"
                size="lg"
                width="hug"
                type="submit"
                form={formId}
                disabled={submitDisabled || isCreatingTdGicApplication}
                loading={isCreatingTdGicApplication}
              >
                {t('td.submitForm')}
              </Button>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" variant="bodyS" align="start">
                {t('td.legalFooter')}
              </Text>
            </GridAlpha.Item>
          </GridAlpha>
          <OnLoadValidateOnlyNonEmptyFields
            validateOnLoad={validateOnLoad}
            setValidateOnLoad={setValidateOnLoad}
          />
        </Form>
      </Card>
    </div>
  );
};

export default TdGicForm;
